import { AsyncPipe, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatSliderModule } from '@angular/material/slider';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { Facet, FacetValue, I18nModule } from '@spartacus/core';
import { KeyboardFocusModule } from '@spartacus/storefront';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { FADE_IN_CONTENT_BY_HEIGHT_OPACITY } from '@app/core/constants/animations.constant';

import { RANGE_INITIAL_STATE } from '../../constants';
import { DpFacetRangeForm } from '../../forms';
import { DpFacetRangeService } from '../../services';
import { FacetRangeFilter } from '../../types';
import { DpFacetComponent } from '../facet.component';

@Component({
    selector: 'dp-facet-range',
    templateUrl: './facet-range.component.html',
    styleUrls: ['../facet.component.scss', './facet-range.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        SlicePipe,
        RouterLink,
        KeyboardFocusModule,
        I18nModule,
        MatIcon,
        FormsModule,
        ReactiveFormsModule,
        MatSliderModule
    ],
    animations: [FADE_IN_CONTENT_BY_HEIGHT_OPACITY]
})
export class DpFacetRangeComponent extends DpFacetComponent implements OnInit {
  @Input()
  set facet(value: Facet) {
    // eslint-disable-next-line no-underscore-dangle
    this._facet = value;
    this.isMultiSelect = !!value.multiSelect;
    this.state$ = this.facetService.getState(value);

    // eslint-disable-next-line no-underscore-dangle
    this._facet.values = [...(value?.values ?? [])].sort((a, b) => Number(a.name ?? '0') - Number(b.name ?? '0'));
    // eslint-disable-next-line no-underscore-dangle
    this.#setFacetRangeConfiguration(this._facet.values);
  }

  form = new DpFacetRangeForm({ ...RANGE_INITIAL_STATE });
  range: FacetRangeFilter = { ...RANGE_INITIAL_STATE };
  selectedRange$ = new BehaviorSubject<FacetRangeFilter>({ ...RANGE_INITIAL_STATE });
  readonly rangedFacetValues$ = this.selectedRange$.pipe(
    map((filter) =>
      filter.from && filter.to ? this.#facetRangeService.getRangedValues(this.facet, filter) : this.facet?.values
    )
  );
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #facetRangeService = inject(DpFacetRangeService);

  get facet(): Facet {
    // eslint-disable-next-line no-underscore-dangle
    return this._facet;
  }

  ngOnInit(): void {
    this.form.fromControlValueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
    this.form.toControlValueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
    this.form.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(
          (a: Partial<FacetRangeFilter>, b: Partial<FacetRangeFilter>) => a.from === b.from && a.to === b.to
        )
      )
      .subscribe((range) =>
        this.selectedRange$.next({
          from: range.from ?? 0,
          to: range.to ?? 0,
          step: this.selectedRange$.value.step
        })
      );
  }

  onApplyRange(): void {
    const queryParams = this.#facetRangeService.generateQueryParams(this.facet, this.form.value);
    if (queryParams) {
      this.#router.navigate([], { relativeTo: this.#activatedRoute, queryParams });
    }
  }

  #setFacetRangeConfiguration(values: FacetValue[] = []): void {
    this.range = this.#facetRangeService.computeRangeState(values);
    const selectedRange = this.#facetRangeService.getSelectedValues(values) ?? this.range;
    this.selectedRange$.next(selectedRange);
    this.form.updateForm(selectedRange, this.range);
  }
}
